/**
 * File: useAnalyticsDashboardController.ts
 * Path: apps/admin/src/features/dashboard/hooks/useAnalyticsDashboardController.ts
 * Description: Website traffic from GA4: the chosen date range, loading and error state, the headline
 * numbers and the daily trend. Reloads whenever the range changes.
 */

import { useEffect, useState } from "react";
import { fetchTrafficReport } from "@/features/dashboard/ga4Service";
import type { DateRange } from "@/features/dashboard/utils/dateRangeUtils";
import {
  EMPTY_TRAFFIC_METRICS,
  lastThirtyDays,
  summarizeTraffic,
  type ChartDataPoint,
  type TrafficMetrics,
} from "@/features/dashboard/utils/ga4Utils";

export const useAnalyticsDashboardController = () => {
  const [dateRange, setDateRange] = useState<DateRange>(() => lastThirtyDays());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<TrafficMetrics>(EMPTY_TRAFFIC_METRICS);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  useEffect(() => {
    const fetchDashboardTelemetry = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const report = await fetchTrafficReport(dateRange);
        const summary = summarizeTraffic(report);
        setMetrics(summary.metrics);
        setChartData(summary.trend);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard telemetry.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardTelemetry();
  }, [dateRange]);

  return { dateRange, setDateRange, isLoading, error, metrics, chartData };
};
